import { useState, useEffect, useSyncExternalStore } from 'react';
import { webDarkTheme, webLightTheme, type Theme } from '@fluentui/react-components';

type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cw-theme-mode';
const DEFAULT_MODE: ThemeMode = 'system';

// Shared listeners for cross-component sync
let listeners: (() => void)[] = [];
function subscribe(fn: () => void) {
  listeners.push(fn);
  return () => { listeners = listeners.filter(l => l !== fn); };
}
function getSnapshot(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  return (localStorage.getItem(STORAGE_KEY) as ThemeMode) || DEFAULT_MODE;
}
function notify() {
  listeners.forEach(fn => fn());
}

function getSystemPreference(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolveTheme(mode: ThemeMode): Theme {
  if (mode === 'system') {
    return getSystemPreference() === 'light' ? webLightTheme : webDarkTheme;
  }
  return mode === 'light' ? webLightTheme : webDarkTheme;
}

export function useTheme() {
  // Sync across all components using this hook via localStorage + listeners
  const mode = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_MODE);

  const setMode = (newMode: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, newMode);
    notify(); // Immediately re-render all subscribers
  };

  const theme = resolveTheme(mode);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => notify();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  return { theme, mode, setMode };
}

export type { ThemeMode };
